import type { HostEvent, StreamSnapshot } from './tauri'

/** Subscribe before reading state, then deliver only notifications newer than it. */
export async function restoreHostSession(
  listen: (handler: (event: HostEvent, revision: number) => void) => Promise<() => void>,
  snapshot: () => Promise<StreamSnapshot | null>,
  restore: (snapshot: StreamSnapshot) => void,
  handle: (event: HostEvent) => void,
  timeoutMs = 8000,
): Promise<() => void> {
  let cancelled = false
  let off: (() => void) | undefined
  let timer: ReturnType<typeof setTimeout> | undefined
  const timeout = new Promise<never>((_, reject) => {
    timer = setTimeout(() => {
      cancelled = true
      off?.()
      reject(new Error('Stream recovery timed out'))
    }, timeoutMs)
  })
  try {
    return await Promise.race([recover(), timeout])
  } finally {
    clearTimeout(timer)
  }

  async function recover(): Promise<() => void> {
    let pending: { event: HostEvent; revision: number }[] | null = []
    off = await listen((event, revision) => {
      if (cancelled) return
      if (pending) pending.push({ event, revision })
      else handle(event)
    })
    if (cancelled) {
      off()
      return () => {}
    }
    try {
      const state = await snapshot()
      if (cancelled) return () => {}
      if (state) restore(state)
      for (const { event, revision } of pending) {
        if (!state || revision > state.revision) handle(event)
      }
      pending = null
      return off
    } catch (error) {
      off()
      throw error
    }
  }
}
